import { FlowButton } from 'components/ui/flow-button';
import HeroBackground from 'components/HeroBackground';
import { Link } from "react-router-dom";
import { Compass, Mail, Phone, Scale, ShieldCheck } from "lucide-react";
import { useReveal } from "hooks/useReveal";
import { useSiteContent } from "hooks/useSiteContent";
import { HERO_CTA_PRIMARY_CLASS, HERO_CTA_SECONDARY_CLASS } from "components/SiteHeroCtas";

const COMPANY_NAME = "Magellan International LLC";
const BUSINESS_ADDRESS = "United States - business mailing address available upon request";
const PRIVACY_EMAIL = "support@magellan-tms.com";
const SUPPORT_EMAIL = "support@magellan-tms.com";
const PUBLIC_PHONE = "(463) 777-2573";
const EFFECTIVE_DATE = "May 5, 2026";
const LEGAL_HERO_SECTION_CLASS = "relative isolate flex min-h-[100svh] w-full items-center overflow-hidden border-b border-ink/8 md:h-screen md:min-h-[680px]";
const PRIVACY_MARQUEE_ITEMS = [
  "Data collection",
  "Company reviews",
  "Applications",
  "Courses",
  "Bookings",
  "Resources",
  "Retention",
  "Security",
  "Privacy requests",
];
const TERMS_MARQUEE_ITEMS = [
  "Website use",
  "Company reviews",
  "Applications",
  "Resources",
  "Courses",
  "Payments",
  "Intellectual property",
  "Acceptable use",
  "Contact",
];

function renderLegalHeroTitle(title) {
  const words = String(title || "").trim().split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 1);

  return words.map((word, index) => (
    <span key={`${word}-${index}`} aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
      <span className={`inline-block ${index >= goldStartIndex ? "italic shimmer-text" : ""}`}>
        {word}
      </span>
    </span>
  ));
}

function getLegalMarqueeItems(title) {
  return title === "Privacy Policy" ? PRIVACY_MARQUEE_ITEMS : TERMS_MARQUEE_ITEMS;
}

function sectionId(title) {
  return String(title || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function BulletList({ items }) {
  if (!items?.length) return null;

  return (
    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
      {items.map((item) => (
        <li
          key={item}
          className="border border-ink/8 bg-ink/[0.025] px-4 py-3 text-sm leading-6 text-copy/52"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function Paragraphs({ paragraphs }) {
  if (!paragraphs?.length) return null;

  return (
    <div className="space-y-4 font-body text-base leading-8 text-copy/54">
      {paragraphs.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </div>
  );
}

function LegalSection({ number, section }) {
  return (
    <section id={sectionId(section.title)} className="scroll-mt-28 border border-ink/8 bg-surface p-6 sm:p-8 transition-colors hover:border-gold/25">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-[56px_minmax(0,1fr)]">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/35 bg-gold/[0.06] font-display text-lg text-accent-ink">
          {String(number).padStart(2, "0")}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-2xl md:text-3xl leading-tight text-ink">{section.title}</h2>
          <div className="mt-5 space-y-6">
            <Paragraphs paragraphs={section.paragraphs} />
            <BulletList items={section.bullets} />
            {section.subsections?.map((subsection) => (
              <div key={subsection.title} className="border-l border-gold/28 bg-canvas px-5 py-5">
                <h3 className="font-display text-xl text-accent-ink">{subsection.title}</h3>
                <div className="mt-4">
                  <Paragraphs paragraphs={subsection.paragraphs} />
                  <BulletList items={subsection.bullets} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function LegalShell({ eyebrow, title, description, icon: Icon, toc = [], children }) {
  const reveal = useReveal();
  const homeHero = useSiteContent("homeHero");
  const heroImageUrl = homeHero.imageUrl;
  const heroImageAlt = homeHero.imageAlt;
  const marqueeItems = getLegalMarqueeItems(title);

  return (
    <main className="min-h-screen bg-canvas text-ink">
      <section data-theme-surface="dark" className={`${LEGAL_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={heroImageUrl} imageAlt={heroImageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <p aria-hidden="true" className="pointer-events-none absolute -right-8 bottom-10 hidden max-w-[92vw] font-display text-[12vw] leading-none tracking-tight text-ink/[0.035] lg:block">
          {title}
        </p>
        <div className="absolute right-10 top-24 hidden h-20 w-20 border-r border-t border-gold/18 lg:block" />
        <div className="absolute bottom-12 left-10 hidden h-20 w-20 border-b border-l border-gold/18 lg:block" />
        <div className="site-content-shell relative z-10">
          <div
            ref={reveal.ref}
            className={`transition duration-700 ${reveal.visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}
          >
            <div className="mb-7 inline-flex items-center gap-3 border border-gold/25 bg-gold/[0.06] px-4 py-2 font-body text-[10px] font-semibold uppercase tracking-[0.28em] text-accent-ink">
              <Icon className="h-4 w-4" aria-hidden="true" />
              {eyebrow}
            </div>
            <h1 aria-label={title} className="max-w-5xl font-display text-5xl font-semibold tracking-tight leading-[0.96] text-ink sm:text-6xl lg:text-7xl xl:text-[6.5rem]">
              {renderLegalHeroTitle(title)}
            </h1>
            <p className="mt-7 max-w-3xl font-body text-base leading-8 text-copy/58 md:text-lg">{description}</p>
            <div className="mt-8 flex flex-wrap gap-3 font-body text-xs text-copy/48">
              <span className="border border-ink/10 bg-ink/[0.035] px-4 py-2 uppercase tracking-[0.18em]">Effective Date: {EFFECTIVE_DATE}</span>
              <span className="border border-ink/10 bg-ink/[0.035] px-4 py-2 uppercase tracking-[0.18em]">Last Updated: {EFFECTIVE_DATE}</span>
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <FlowButton to="/#contact" className={HERO_CTA_PRIMARY_CLASS}>
                Contact Magellan
              </FlowButton>
              <FlowButton to="/" className={HERO_CTA_SECONDARY_CLASS}>
                Back to Website
              </FlowButton>
            </div>
          </div>
        </div>
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...marqueeItems, ...marqueeItems].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 whitespace-nowrap px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <section className="py-16">
        <div className="site-content-shell grid gap-10 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-6">{children}</div>
          <aside className="h-fit border border-gold/18 bg-surface p-6 lg:sticky lg:top-24">
            <Link to="/" className="mb-7 inline-flex items-center gap-3">
              <Compass className="h-5 w-5 text-accent-ink" strokeWidth={1.5} />
              <span className="font-display text-lg font-bold tracking-[0.3em] text-ink">MAGELLAN</span>
            </Link>

            <div className="border-t border-ink/8 pt-6">
              <p className="font-body text-[10px] font-semibold uppercase tracking-[0.26em] text-accent-ink">Contact</p>
              <div className="mt-5 space-y-4 font-body text-sm leading-6 text-copy/52">
                <p className="text-copy/68">{COMPANY_NAME}</p>
                <p>{BUSINESS_ADDRESS}</p>
                <a className="flex items-center gap-3 text-copy/58 hover:text-accent-ink" href={`mailto:${SUPPORT_EMAIL}`}>
                  <Mail className="h-4 w-4 text-accent-ink" strokeWidth={1.5} />
                  {SUPPORT_EMAIL}
                </a>
                <a className="flex items-center gap-3 text-copy/58 hover:text-accent-ink" href="tel:+14637772573">
                  <Phone className="h-4 w-4 text-accent-ink" strokeWidth={1.5} />
                  {PUBLIC_PHONE}
                </a>
              </div>
            </div>

            {toc.length ? (
              <div className="mt-8 border-t border-ink/8 pt-6">
                <p className="font-body text-[10px] font-semibold uppercase tracking-[0.26em] text-accent-ink">Document index</p>
                <nav className="mt-5 max-h-[46vh] space-y-2 overflow-y-auto pr-2">
                  {toc.map((section, index) => (
                    <a
                      key={section.title}
                      href={`#${sectionId(section.title)}`}
                      className="group flex items-start gap-3 border-b border-ink/6 pb-2 font-body text-xs leading-5 text-copy/40 transition-colors hover:text-accent-ink"
                    >
                      <span className="text-accent-ink/45">{String(index + 1).padStart(2, "0")}</span>
                      <span>{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            ) : null}

            <FlowButton
              to="/#contact"
              className="mt-7 inline-flex w-full items-center justify-center gap-2  px-5 py-3 font-body text-[10px] font-bold uppercase tracking-[0.22em]  transition "
            >
              Contact Magellan
            </FlowButton>
          </aside>
        </div>
      </section>
    </main>
  );
}

const privacySections = [
  {
    title: "Who We Are",
    paragraphs: [
      "Magellan International LLC provides operational systems, documentation frameworks, training materials, workflow design, courses, implementation support, and related services for trucking companies and transportation businesses.",
      "Our work may include support related to compliance paperwork, Driver Qualification Files, safety systems, driver training, recruiting workflows, TMS operations, specialized freight procedures, company operating reviews, courses, and internal documentation systems.",
      `For privacy questions, contact ${COMPANY_NAME} at ${BUSINESS_ADDRESS} or ${PRIVACY_EMAIL}.`,
    ],
  },
  {
    title: "Information We Collect",
    paragraphs: [
      "We collect information in several ways depending on how you use the website. You may provide information directly when you submit a company review, request a company operating review, book a meeting, register for a course, request private company training, download a resource, subscribe to updates, submit a contact form, apply to work with Magellan, send us an email, or communicate with us by phone, message, or another channel.",
      "Do not submit sensitive personal information unless it is specifically requested and necessary for the relevant purpose.",
    ],
    bullets: [
      "Name, company name, job title or role, email address, phone number, company website, company location, and meeting preferences",
      "DOT number, MC number, fleet size, number of trucks, number of trailers, number of drivers, freight type, and operating lanes",
      "Current TMS, current ELD provider, paperwork systems, compliance systems, safety information, training information, recruiting information, and dispatch information",
      "Course registration details, payment-related transaction details, messages, notes, uploaded files, resumes, work history, portfolios, work samples, availability, and role interests",
    ],
    subsections: [
      {
        title: "Company review information",
        paragraphs: [
          "When a trucking company applies for a Magellan review, we may collect operational information about the company. This information is used to evaluate the company request, prepare for a meeting, recommend an appropriate service path, and provide operational support if an engagement is accepted.",
        ],
        bullets: [
          "Company identity and contact information, DOT and MC information, fleet profile, driver model, freight type, current systems, paperwork condition, compliance concerns, safety and training structure, recruiting process, driver onboarding workflow, TMS usage, operational problems, urgency, and additional notes",
        ],
      },
      {
        title: "Employee, contractor, and applicant information",
        paragraphs: [
          "When you apply to work with Magellan, we may collect information to evaluate applicants, communicate about roles, conduct screening, assign test tasks, schedule interviews, and maintain hiring records.",
          "Submitting an application does not guarantee employment, contractor work, project work, interview selection, or future engagement.",
        ],
        bullets: [
          "Full name, email address, phone number, city, country, time zone, resume, LinkedIn profile, portfolio, work sample, role interests, availability, work history, English level, tool experience, writing samples, application responses, interview notes, and screening-task results",
        ],
      },
      {
        title: "Course, training, payment, booking, and website information",
        paragraphs: [
          "If you register for a course or request company training, we may collect registration, attendance, payment, question, and follow-up information. If you purchase a course, training session, resource, service, consultation, or other paid offering, payment may be processed through third-party providers such as Stripe, PayPal, or another payment processor.",
          "Magellan does not intend to store full credit card numbers on its website. Payment providers may collect and process payment card details, billing information, transaction identifiers, and fraud-prevention data according to their own privacy policies and terms.",
          "When you book a meeting, calendar or scheduling tools may process your name, email, phone, company, meeting type, selected date and time, time zone, notes, invitation details, rescheduling activity, and cancellation activity. When you visit the website, we or our providers may collect usage and device information to operate and improve the site.",
        ],
        bullets: [
          "Payment status, transaction ID, amount paid, transaction date, billing name, billing email, last four card digits if provided by the processor, and processor confirmation",
          "IP address, browser type, device type, operating system, pages viewed, referring website, time spent on pages, approximate location, click activity, form interaction data, cookie identifiers, analytics events, and error logs",
        ],
      },
    ],
  },
  {
    title: "Cookies and Similar Technologies",
    paragraphs: [
      "The website may use cookies, pixels, local storage, analytics tools, or similar technologies. These technologies may keep the website functional, remember preferences, analyze traffic, measure page performance, understand visitor behavior, improve user experience, track marketing effectiveness, and support security and fraud prevention.",
      "You can usually control cookies through your browser settings. If you block cookies, some website features may not work correctly. If Magellan uses analytics, advertising pixels, or marketing tools, those tools may set cookies or collect usage information according to their own policies.",
    ],
  },
  {
    title: "How We Use Information",
    paragraphs: [
      "We may use collected information to operate the website, respond to messages and inquiries, review company reviews, prepare for company review meetings, provide service recommendations, schedule meetings, deliver courses and training, process registrations and payments, send course access details, send resources or requested materials, evaluate job applicants and contractors, conduct screening and hiring steps, communicate about services, provide customer support, improve website content and functionality, develop new services, courses, articles, and resources, maintain internal records, protect against fraud, misuse, spam, or security threats, comply with legal, accounting, tax, and operational obligations, and enforce our terms and policies.",
      "We may also use information for internal business analysis, service improvement, client intake management, content planning, and operational reporting.",
    ],
  },
  {
    title: "Legal Bases for Processing",
    paragraphs: [
      "Depending on where you are located, we may rely on one or more legal bases to process personal information, including consent, performance of a contract, steps taken before entering a contract, legitimate business interests, compliance with legal obligations, and protection of rights, safety, and security.",
      "Examples include processing company review information to evaluate service requests, employee application information to evaluate potential work relationships, course information to register attendees, payment information to complete transactions, website analytics to improve the website, and records where necessary for tax, accounting, legal, or operational requirements.",
    ],
  },
  {
    title: "How We Share Information",
    paragraphs: [
      "We do not sell personal information in the ordinary meaning of selling contact lists for money. We may share information with trusted service providers and business tools that help us operate Magellan, and we share only the information reasonably necessary for those purposes.",
      "Service providers are expected to use information only to provide services to Magellan or as otherwise permitted by law and their applicable agreements.",
    ],
    bullets: [
      "Website hosting providers, email service providers, calendar and booking tools, payment processors, CRM or lead-management tools, analytics providers, cloud storage providers, document-management tools, course platforms, contractors or team members helping with client intake, operations, support, or hiring, professional advisors, and legal authorities when required by law",
    ],
  },
  {
    title: "No Sale of Personal Information",
    paragraphs: [
      "Magellan does not intend to sell your personal information. If Magellan ever uses advertising, analytics, or tracking tools that may be considered a sale, sharing, or targeted advertising under certain privacy laws, we will provide any required opt-out mechanisms where applicable.",
    ],
  },
  {
    title: "Email, Phone, and Marketing Communications",
    paragraphs: [
      "If you submit your contact information, we may contact you about your inquiry, company review, meeting request, course registration, employee application, requested resources, service opportunities, training opportunities, and related Magellan updates.",
      "You may unsubscribe from marketing emails by using the unsubscribe link in the email, if available, or by contacting us. Transactional, administrative, legal, or service-related messages may still be sent when necessary.",
      "If SMS or text messaging is used, message and data rates may apply. You may opt out of promotional text messages by following the instructions provided in the message or contacting us.",
    ],
  },
  {
    title: "Data Retention",
    paragraphs: [
      "We retain personal information only as long as reasonably necessary for the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law. Retention periods may depend on the type of information, whether you are a client, applicant, attendee, or visitor, whether a service relationship exists, and tax, accounting, legal, dispute-resolution, security, fraud-prevention, recordkeeping, and operational needs.",
      "Company review records may be retained to manage inquiries, prepare proposals, maintain service history, or support future follow-up. Employee application records may be retained for hiring, screening, future role consideration, or employment-related obligations. Course registration records may be retained for attendance, payment, follow-up, and training administration. Payment records may be retained for accounting, tax, chargeback, and legal purposes. Website analytics data may be retained in aggregated or identifiable form depending on the tool used.",
      "You may request deletion of your information, but we may retain certain information where required or permitted by law.",
    ],
  },
  {
    title: "Security",
    paragraphs: [
      "We use reasonable administrative, technical, and organizational measures designed to protect personal information. No website, email system, cloud storage system, payment processor, or internet transmission is completely secure. We cannot guarantee absolute security.",
      `You should avoid submitting unnecessary sensitive information through website forms or email. If you believe your information has been submitted incorrectly or may have been compromised, contact us promptly at ${PRIVACY_EMAIL}.`,
    ],
  },
  {
    title: "International Visitors and Data Transfers",
    paragraphs: [
      "Magellan may work with visitors, applicants, contractors, service providers, and clients located in different countries. If you access the website or submit information from outside the United States, your information may be processed in the United States or other countries where Magellan, its service providers, or business tools operate.",
      "Those countries may have data protection laws different from the laws in your location. Where required, Magellan will take appropriate steps to protect personal information in connection with international transfers.",
    ],
  },
  {
    title: "Your Privacy Rights",
    paragraphs: [
      `Depending on your location and applicable law, you may have rights regarding your personal information. To exercise privacy rights, contact us at ${PRIVACY_EMAIL}. We may need to verify your identity before fulfilling a request. We may deny or limit requests where permitted by law, including where information is needed for legal, security, tax, accounting, dispute-resolution, service, or operational purposes.`,
    ],
    bullets: [
      "Request access to personal information we hold about you",
      "Request correction of inaccurate information",
      "Request deletion of personal information",
      "Request restriction of processing",
      "Object to certain processing",
      "Request data portability",
      "Withdraw consent where processing is based on consent",
      "Opt out of certain marketing communications",
      "Opt out of certain sales, sharing, or targeted advertising where applicable",
      "Appeal certain privacy decisions where applicable",
      "File a complaint with a data protection authority where applicable",
    ],
  },
  {
    title: "California and U.S. State Privacy Notice",
    paragraphs: [
      `Depending on your state of residence and whether applicable legal thresholds are met, you may have additional rights under state privacy laws. Magellan does not intend to sell personal information. If you believe a state privacy law applies to your information, contact us at ${PRIVACY_EMAIL}.`,
    ],
    bullets: [
      "Right to know what personal information is collected",
      "Right to access personal information",
      "Right to correct inaccurate information",
      "Right to delete personal information",
      "Right to opt out of sale, sharing, or targeted advertising",
      "Right to limit certain sensitive personal information uses, where applicable",
      "Right to non-discrimination for exercising privacy rights",
      "Right to appeal a privacy-rights decision",
    ],
  },
  {
    title: "Job Applicant Privacy",
    paragraphs: [
      "If you apply to work with Magellan, we use your application information to evaluate your qualifications, role fit, availability, skills, work samples, and potential engagement. We may share applicant information internally with people involved in screening, hiring, testing, interviewing, or contractor selection.",
      "We may retain applicant information for current or future opportunities unless you request deletion, subject to applicable legal and operational requirements. Submitting an application does not create an employment relationship, contractor relationship, offer, guarantee of interview, or guarantee of future work.",
    ],
  },
  {
    title: "Client and Business Information",
    paragraphs: [
      "Company reviews may contain business information about trucking operations, compliance structure, fleet size, systems, processes, risks, or internal conditions.",
      "Magellan treats submitted business information as confidential business intake information, but unless a separate written agreement is signed, submission through the website does not create a formal client relationship, attorney-client relationship, privileged relationship, or exclusive engagement.",
      "A separate proposal, statement of work, service agreement, or written engagement may be required before services begin.",
    ],
  },
  {
    title: "Children's Privacy",
    paragraphs: [
      "The website is intended for business users, company representatives, job applicants, and adults. It is not intended for children under 13 years old, and we do not knowingly collect personal information from children under 13.",
      "If you believe a child submitted personal information to us, contact us and we will take appropriate steps to delete it.",
    ],
  },
  {
    title: "Third-Party Links and Tools",
    paragraphs: [
      "The website may link to third-party websites, tools, booking platforms, payment processors, course tools, social media platforms, or resource providers. We are not responsible for the privacy practices, content, security, or policies of third-party websites or services.",
      "Review the privacy policy of each third-party service before submitting information.",
    ],
  },
  {
    title: "Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. When we update it, we will revise the Last Updated date at the top of the page.",
      "Your continued use of the website after changes are posted means you acknowledge the updated Privacy Policy.",
    ],
  },
  {
    title: "Contact Us",
    paragraphs: [
      `For questions about this Privacy Policy or privacy requests, contact ${COMPANY_NAME}, ${BUSINESS_ADDRESS}, Email: ${PRIVACY_EMAIL}, Phone: ${PUBLIC_PHONE}.`,
    ],
  },
];

const termsSections = [
  {
    title: "About Magellan",
    paragraphs: [
      "Magellan provides operational systems, documentation frameworks, workflow design, training materials, courses, implementation support, and related services for trucking companies and transportation businesses.",
      "Magellan is not a government agency and is not affiliated with the FMCSA, DOT, DataQs, any state enforcement agency, court, insurer, shipper, or regulatory authority unless expressly stated in writing.",
    ],
    bullets: [
      "Compliance paperwork structure, Driver Qualification File systems, safety systems, driver training systems, driver recruiting workflows, TMS operations and workflow design, specialized freight procedures, courses and company training, operational documentation, SOPs, checklists, trackers, dashboards, and company operating reviews",
    ],
  },
  {
    title: "No Legal, Regulatory, Tax, Insurance, or Professional Advice",
    paragraphs: [
      "The website, blog, resources, courses, forms, checklists, templates, and general materials are provided for informational, educational, and operational purposes only.",
      "Magellan does not replace legal counsel, regulatory counsel, tax advisors, insurance advisors, safety consultants, or qualified professionals where those services are required.",
      "Carriers remain responsible for reviewing their own facts, records, operations, policies, driver relationships, contractor relationships, employment classifications, regulatory obligations, shipper requirements, insurance obligations, and legal duties.",
      "You should consult qualified counsel or appropriate professionals before relying on any material for legal, regulatory, tax, employment, insurance, or case-specific decisions.",
    ],
    bullets: [
      "No legal advice, regulatory advice, tax advice, insurance advice, employment-law advice, court representation, FMCSA or DOT representation, DataQs representation, compliance guarantee, audit guarantee, or outcome guarantee",
    ],
  },
  {
    title: "No Guarantee of Compliance, Audit Results, DataQs Results, or Business Outcomes",
    paragraphs: [
      "Magellan may provide operational support, documentation frameworks, training, templates, workflows, and implementation guidance. However, Magellan does not guarantee compliance, audit passage, DataQs acceptance, CSA score changes, insurance approval, shipper approval, driver retention, hiring outcomes, revenue growth, safety performance, claim outcomes, court outcomes, regulatory outcomes, or business results.",
      "Any examples, templates, courses, checklists, or workflows are general operational tools and must be adapted to your company's actual facts, operations, legal obligations, and professional guidance.",
    ],
  },
  {
    title: "Website Content",
    paragraphs: [
      "All website content is provided for general informational and business purposes. We try to keep the website accurate and useful, but we do not guarantee that all content is complete, current, error-free, or suitable for your specific situation.",
      "Trucking regulations, enforcement practices, customer requirements, insurance expectations, technology tools, and business conditions may change. You are responsible for verifying current requirements before acting.",
    ],
  },
  {
    title: "Company Reviews",
    paragraphs: [
      "The company review is for trucking companies, transportation businesses, carrier owners, managers, or authorized representatives requesting Magellan services or review. Magellan may accept, reject, prioritize, delay, or decline any company review at its discretion.",
    ],
    bullets: [
      "You are authorized to submit information on behalf of the company",
      "The information you provide is accurate to the best of your knowledge",
      "You will not submit false, misleading, unlawful, or confidential third-party information without authorization",
      "Submission does not guarantee acceptance as a client",
      "Submission does not create a client relationship by itself",
      "A separate agreement, proposal, invoice, statement of work, or written engagement may be required before services begin",
    ],
  },
  {
    title: "Employee, Contractor, and Work Applications",
    paragraphs: [
      "The Work With Us application is for individuals applying for employment, contractor work, project work, sales support, documentation support, training support, writing work, operations support, or other roles with Magellan. Magellan may use screening questions, interviews, test assignments, trial projects, reference checks, or other evaluation methods where appropriate.",
    ],
    bullets: [
      "Your application information is accurate",
      "Your resume, work samples, and written answers are your own or properly authorized",
      "You will not submit false, misleading, unlawful, or confidential third-party information",
      "Submission does not guarantee employment, contractor work, an interview, a paid test task, or future engagement",
      "Magellan may reject or retain applications at its discretion",
      "A separate written agreement may be required before any work relationship begins",
    ],
  },
  {
    title: "Booking Meetings",
    paragraphs: [
      "The website may allow you to book meetings through a scheduling tool. Booking a meeting does not guarantee acceptance as a client, availability of services, specific advice, immediate support, project acceptance, employment consideration, course enrollment, or any particular outcome.",
      "Magellan may reschedule, cancel, or decline meetings at its discretion. You agree to provide accurate booking information and to cancel or reschedule if you cannot attend.",
    ],
  },
  {
    title: "Courses and Training",
    paragraphs: [
      "Magellan may offer public courses, paid courses, private company training, recorded training, resources, or educational sessions. Magellan may remove participants who disrupt a session, misuse materials, share unauthorized access, or violate these Terms.",
    ],
    bullets: [
      "Training is educational and operational only",
      "Training does not constitute legal, regulatory, tax, insurance, or professional advice",
      "Training does not guarantee any outcome",
      "You will not record, reproduce, distribute, resell, or publicly share course materials without written permission",
      "Access links, slides, worksheets, checklists, recordings, and materials are for registered participants or authorized company users only",
      "Magellan may update, reschedule, cancel, modify, or replace training content when necessary",
    ],
  },
  {
    title: "Payments, Pricing, and Refunds",
    paragraphs: [
      "Prices for courses, services, resources, training, or consultations may be listed on the website or provided separately by proposal, invoice, or written communication. Unless otherwise stated in writing, prices are listed in U.S. dollars, payment may be required before access or service delivery, payment processing may be handled by third-party providers, Magellan may change prices for future purchases, a purchase is not complete until payment is accepted, and Magellan may refuse or cancel a transaction where fraud, error, misuse, or unauthorized activity is suspected.",
    ],
    subsections: [
      {
        title: "Course refunds",
        paragraphs: [
          "Unless a specific course page states a different refund policy, course payments are non-refundable once access details, materials, recordings, or live-session access have been provided. Magellan may, at its discretion, allow a registration transfer to another attendee from the same company. If Magellan cancels a paid course and does not provide a replacement session or recording, Magellan may provide a refund or credit. No refund is guaranteed for missed attendance if access was provided.",
        ],
      },
      {
        title: "Service payments",
        paragraphs: [
          "Service fees, retainers, implementation fees, consulting fees, or project fees are governed by the applicable invoice, statement of work, proposal, or written agreement. If there is a conflict between these Terms and a signed written agreement, the signed written agreement controls for that specific engagement.",
        ],
      },
    ],
  },
  {
    title: "Resources, Templates, Checklists, and Downloads",
    paragraphs: [
      "Magellan may provide free or paid resources, including guides, checklists, templates, worksheets, SOP outlines, training materials, blog articles, course materials, forms, and operational examples. These materials are provided for general business and educational use.",
      "You are responsible for reviewing, editing, adapting, and validating materials before using them in your company. Magellan does not guarantee that any template, checklist, form, or guide is complete, legally sufficient, regulator-approved, court-approved, insurer-approved, shipper-approved, or suitable for your specific operation.",
    ],
  },
  {
    title: "User Submissions",
    paragraphs: [
      "If you submit information, documents, comments, applications, messages, forms, files, work samples, or other materials to Magellan, you represent that you have the right to submit the material, it is accurate to the best of your knowledge, it does not violate any law or third-party right, it does not contain malware or harmful code, it does not contain unnecessary sensitive personal information, and it does not include confidential third-party information unless you are authorized to share it.",
      "You grant Magellan permission to use submitted materials for the purpose for which they were submitted, including reviewing applications, responding to inquiries, preparing for meetings, evaluating work candidates, providing services, delivering training, processing payments, maintaining records, and operating the business.",
    ],
  },
  {
    title: "Confidentiality and Client Information",
    paragraphs: [
      "Magellan understands that company reviews and service inquiries may include business-sensitive information. However, submitting information through the website does not, by itself, create attorney-client privilege, legal privilege, a formal client relationship, a fiduciary relationship, a confidentiality agreement, or an exclusive engagement.",
      "Confidentiality obligations for paid services may be addressed in a separate written agreement, proposal, statement of work, or service contract. You should not submit highly sensitive, legally privileged, regulated, or confidential third-party information through the website unless necessary and authorized.",
    ],
  },
  {
    title: "Acceptable Use",
    paragraphs: [
      "You agree not to misuse the website, forms, resources, courses, or Magellan materials. Magellan may block access, reject submissions, cancel registrations, or take appropriate action if these Terms are violated.",
    ],
    bullets: [
      "Do not violate any law or regulation, submit false or unauthorized information, interfere with website security, attempt unauthorized access, upload malware, scrape content without permission, impersonate another person or company, misrepresent your authority, submit spam or abusive messages, harass or threaten others, use materials to create a competing product or service without authorization, or resell, redistribute, or publicly post paid materials without permission",
    ],
  },
  {
    title: "Intellectual Property",
    paragraphs: [
      "The website and its content are owned by Magellan or its licensors and are protected by intellectual property laws. This includes website text, branding, logos, service descriptions, blog articles, resource materials, course materials, training materials, checklists, templates, frameworks, graphics, page layouts, videos, downloads, and operational documents.",
      "You may view website content for your personal or internal business evaluation. You may not copy, reproduce, modify, publish, sell, license, distribute, create derivative works from, or exploit Magellan content without written permission, except where a resource expressly permits internal business use.",
      "Paid course materials and training materials may not be shared outside the registered attendee or authorized company group unless Magellan gives written permission.",
    ],
  },
  {
    title: "Limited License for Resources",
    paragraphs: [
      "If Magellan provides a downloadable checklist, template, or resource, Magellan grants you a limited, revocable, non-exclusive, non-transferable license to use that material for your own internal business purposes.",
    ],
    bullets: [
      "You may not resell the material, publish it publicly, claim it as your own original product, remove Magellan branding where branding is included, use it to create a competing template pack, training product, course, or consulting product, or distribute it to third parties outside your company without permission",
    ],
  },
  {
    title: "Third-Party Services and Links",
    paragraphs: [
      "The website may link to or integrate with third-party services, including payment processors, scheduling tools, course platforms, email tools, analytics tools, social media platforms, government websites, resource providers, external articles, and client tools.",
      "Magellan is not responsible for the content, policies, availability, security, or practices of third-party services. Your use of third-party services may be governed by their own terms and privacy policies.",
    ],
  },
  {
    title: "Disclaimers",
    paragraphs: [
      "The website and materials are provided on an as is and as available basis. To the fullest extent permitted by law, Magellan disclaims all warranties, express or implied, including warranties of accuracy, completeness, currentness, merchantability, fitness for a particular purpose, non-infringement, availability, security, error-free operation, and suitability for a specific carrier, audit, claim, ticket, filing, inspection, shipper, insurer, or business situation.",
      "Magellan does not warrant that the website will be uninterrupted, secure, or free from errors, viruses, or harmful components.",
    ],
  },
  {
    title: "Limitation of Liability",
    paragraphs: [
      "To the fullest extent permitted by law, Magellan and its owners, officers, employees, contractors, agents, service providers, and affiliates will not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including damages for lost profits, lost revenue, lost business opportunities, lost data, business interruption, compliance outcomes, audit outcomes, DataQs outcomes, CSA score changes, insurance decisions, shipper decisions, employment decisions, operational losses, use or inability to use website materials, reliance on website content, third-party service issues, or unauthorized access or security incidents beyond Magellan's reasonable control.",
      "To the fullest extent permitted by law, Magellan's total liability for claims related to the website, content, courses, resources, or these Terms will not exceed the amount you paid to Magellan for the specific product or service giving rise to the claim, or one hundred U.S. dollars ($100), whichever is greater.",
      "Some jurisdictions do not allow certain limitations of liability, so some limitations may not apply to you.",
    ],
  },
  {
    title: "Indemnification",
    paragraphs: [
      "You agree to defend, indemnify, and hold harmless Magellan and its owners, officers, employees, contractors, agents, service providers, and affiliates from and against claims, damages, liabilities, losses, costs, and expenses, including reasonable attorneys' fees, arising from or related to your use of the website, your violation of these Terms, your submission of false or unauthorized information, your violation of law, your violation of third-party rights, your misuse of Magellan materials, your unauthorized sharing of course or training materials, or your reliance on website content without appropriate professional review.",
    ],
  },
  {
    title: "Termination or Restriction of Access",
    paragraphs: [
      "Magellan may restrict, suspend, or terminate your access to the website, forms, resources, courses, or communications at any time if we believe you violated these Terms, submitted abusive or fraudulent information, misused materials, disrupted services, or created risk for Magellan or others.",
    ],
  },
  {
    title: "Governing Law",
    paragraphs: [
      "These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles, unless applicable law requires otherwise.",
      "Any dispute arising from or related to these Terms, the website, or Magellan's online materials will be handled in the courts located in Delaware, unless a separate written agreement states otherwise or applicable law requires a different venue.",
    ],
  },
  {
    title: "Changes to These Terms",
    paragraphs: [
      "Magellan may update these Terms from time to time. When we update them, we will revise the Last Updated date at the top of the page.",
      "Your continued use of the website after updated Terms are posted means you accept the updated Terms.",
    ],
  },
  {
    title: "Contact",
    paragraphs: [
      `For questions about these Terms, contact ${COMPANY_NAME}, ${BUSINESS_ADDRESS}, Email: ${SUPPORT_EMAIL}, Phone: ${PUBLIC_PHONE}.`,
    ],
  },
];

export function PrivacyPolicyPage() {
  return (
    <LegalShell
      eyebrow="Privacy Policy"
      title="Privacy Policy"
      description="How Magellan collects, uses, protects, and retains information submitted through the website, company reviews, applications, courses, bookings, and resources."
      icon={ShieldCheck}
      toc={privacySections}
    >
      <section className="border border-gold/22 bg-gold/[0.055] p-6 font-body text-base leading-8 text-copy/70 sm:p-8">
        <p>
          This Privacy Policy applies to <span className="font-semibold">https://magellan.international</span> and any
          related pages, forms, booking links, course registration pages, blog pages, resource pages, and application
          forms operated by or on behalf of Magellan. If you do not agree with this Privacy Policy, do not use the
          website or submit information through our forms.
        </p>
      </section>
      {privacySections.map((section, index) => (
        <LegalSection key={section.title} number={index + 1} section={section} />
      ))}
    </LegalShell>
  );
}

export function TermsOfUsePage() {
  return (
    <LegalShell
      eyebrow="Terms of Use"
      title="Terms of Use"
      description="The rules for using Magellan's website, resources, courses, forms, company reviews, applications, and online materials."
      icon={Scale}
      toc={termsSections}
    >
      <section className="border border-gold/22 bg-gold/[0.055] p-6 font-body text-base leading-8 text-copy/70 sm:p-8">
        <p>
          By using the website, submitting a form, booking a meeting, registering for a course, downloading a resource,
          applying to work with Magellan, or communicating with us through the website, you agree to these Terms. If you
          do not agree, do not use the website.
        </p>
      </section>
      {termsSections.map((section, index) => (
        <LegalSection key={section.title} number={index + 1} section={section} />
      ))}
    </LegalShell>
  );
}
